import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, CheckCircle, XCircle, Clock, Trash2, Upload } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface Connection {
  id: string;
  name: string;
  connector_id: string;
  status: string;
  last_tested?: string;
  created_at: string;
}

const EXTERNAL_CONNECTORS = ['connectwise', 'autotask', 'it_glue'];

export default function ConnectionsPage() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConnections();
  }, []);

  async function loadConnections() {
    try {
      const data = await api.get<{ connections: Connection[] }>('/connections');
      setConnections(data.connections);
    } catch {}
    setLoading(false);
  }

  async function deleteConnection(id: string) {
    if (!confirm('Delete this connection?')) return;
    try {
      await api.delete(`/connections/${id}`);
      setConnections(prev => prev.filter(c => c.id !== id));
    } catch {}
  }

  async function testConnection(id: string) {
    try {
      const result = await api.post<{ success: boolean; message: string }>(`/connections/${id}/test`);
      alert(result.message);
      loadConnections();
    } catch (err: any) {
      alert(err.message);
    }
  }

  const statusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle size={18} color="#10b981" />;
      case 'error': return <XCircle size={18} color="#ef4444" />;
      default: return <Clock size={18} color="#6b7280" />;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#111827' }}>Connections</h2>
        <Link to="/connections/new" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', backgroundColor: ACCENT, color: '#fff', borderRadius: '0.375rem', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500 }}>
          <Plus size={16} /> New Connection
        </Link>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading...</p>
      ) : connections.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>No connections configured yet.</p>
          <Link to="/connections/new" style={{ color: ACCENT }}>Create your first connection</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {connections.map(conn => (
            <div key={conn.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
              {statusIcon(conn.status)}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{conn.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {conn.connector_id} &middot; {conn.status}
                  {conn.last_tested && ` &middot; Tested ${new Date(conn.last_tested).toLocaleDateString()}`}
                </div>
              </div>
              {EXTERNAL_CONNECTORS.includes(conn.connector_id) && (
                <button onClick={() => navigate(`/connections/${conn.id}/import`)} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.375rem 0.75rem', fontSize: '0.8rem', border: `1px solid ${ACCENT}`, borderRadius: '0.25rem', backgroundColor: '#fff7ed', color: ACCENT, cursor: 'pointer', fontWeight: 500 }}>
                  <Upload size={14} /> Import
                </button>
              )}
              <button onClick={() => testConnection(conn.id)} style={{ padding: '0.375rem 0.75rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', backgroundColor: '#fff', cursor: 'pointer' }}>
                Test
              </button>
              <button onClick={() => deleteConnection(conn.id)} style={{ padding: '0.375rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#ef4444' }}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
